<template>
  <div
    class="min-h-screen sm:h-screen sm:max-h-screen flex flex-col items-center pt-[4vh] sm:pt-0 sm:justify-center px-6 lg:px-8 safe-area-inset sm:overflow-hidden">
    <div class="w-full sm:max-w-[40vh] sm:max-h-[95vh] sm:flex sm:flex-col">
      <!-- Loading State -->
      <div v-if="loading" class="text-center pt-[10vh]">
        <div
          class="w-12 h-12 sm:w-16 sm:h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4">
        </div>
        <p class="text-white text-base sm:text-lg">{{ t("home.loading") }}</p>
      </div>

      <!-- Content -->
      <template v-else-if="generation">
        <!-- Generated with header -->
        <div class="text-center mb-8 sm:mb-4">
          <p class="text-white/80 text-sm font-medium tracking-wide uppercase mb-2 sm:mb-1">
            {{ t("home.generatedWith") }}
          </p>
          <div class="flex items-center justify-center mx-auto">

            <img src="../../assets/images/logo_w_text.png" alt="ReFace" class="h-12 sm:h-10" />
            <p class="text-white text-md font-semibold ml-1">EPICFACE</p>
          </div>
        </div>

        <!-- Generation Image (Square) with Model Name overlay -->
        <div class="relative mb-4 sm:mb-4 glow-effect sm:flex-1 sm:min-h-0">
          <!-- Sparkles -->
          <div class="sparkles">
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
            <div class="sparkle"></div>
          </div>
          <div class="sparkles">
            <div class="sparkle-star"></div>
            <div class="sparkle-star"></div>
            <div class="sparkle-star"></div>
            <div class="sparkle-star"></div>
          </div>
          <div class="sm:h-full sm:flex sm:items-center sm:justify-center">
            <div class="relative">
              <!-- Image -->
              <img v-if="isImage" :src="generation.url" alt="Generation"
                class="w-full aspect-square sm:w-auto sm:h-full sm:max-h-full sm:aspect-square rounded-xl sm:rounded-3xl shadow-lg object-cover relative z-10 border-2 border-white" />
              <!-- Video -->
              <video v-else :src="generation.url" autoplay loop muted playsinline
                class="w-full aspect-square sm:w-auto sm:h-full sm:max-h-full sm:aspect-square rounded-xl sm:rounded-3xl shadow-lg object-cover relative z-10 border-2 border-white"></video>
              <div class="shimmer-overlay rounded-xl sm:rounded-3xl"></div>
              <!-- Model Name overlay -->
              <p
                class="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 text-lg font-medium text-white px-2 py-1 rounded-lg drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] z-20">
                {{ modelName }}
              </p>
            </div>
          </div>
        </div>

        <!-- Reaction Buttons -->
        <template v-if="!hasVoted">
          <p class="text-white text-center text-lg sm:text-base font-bold mb-3 sm:mb-2 opacity-80">
            {{ t("home.voteNow") }}
          </p>
        </template>
        <div class="relative z-30 w-full flex justify-between mb-6 sm:mb-3">
          <button v-for="emoji in emojis" :key="emoji" type="button"
            class="reaction-btn flex flex-col items-center justify-center w-16 h-20 sm:w-14 sm:h-18 rounded-2xl backdrop-blur-sm border transition-all duration-200 active:scale-95 sm:hover:scale-110"
            :class="[
              selectedEmoji === emoji
                ? 'bg-yellow-400/30 border-yellow-400'
                : 'bg-white/10 border-white/20 active:bg-white/25 sm:hover:bg-white/20 sm:hover:border-white/40',
            ]" @click="vote(emoji)">
            <span class="text-3xl sm:text-4xl">{{ emoji }}</span>
            <span class="text-white/80 text-xs font-medium mt-1">{{
              reactionCounts[emoji] || 0
            }}</span>
          </button>
        </div>

        <!-- Post-vote CTA (after vote) -->
        <div v-if="hasVoted" class="text-center mb-6 sm:mb-3 post-vote-cta">
          <!-- <p class="text-white text-2xl sm:text-xl font-bold mb-2 sm:mb-1">
            {{ t("home.thankYou") }} {{ selectedEmoji }}
          </p> -->
          <p class="text-yellow-300 text-xl sm:text-lg font-bold animate-pulse-text">
            {{ t("home.yourTurn") }}
          </p>
          <div class="bounce-arrow text-yellow-400 text-3xl sm:text-2xl mt-1 sm:mt-1">
            ↓
          </div>
        </div>

        <!-- CTA Button -->
        <button @click="openApp"
          class="relative z-30 w-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 active:from-yellow-500 active:via-yellow-400 active:to-yellow-600 sm:hover:from-yellow-500 sm:hover:via-yellow-400 sm:hover:to-yellow-600 text-white font-extrabold py-3 px-6 sm:py-2 sm:px-6 rounded-xl sm:rounded-2xl shadow-lg transition duration-300 sm:transform sm:hover:scale-105"
          type="button">
          <p class="text-xl sm:text-lg text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
            {{ t("home.tryItYourself") }}
          </p>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { onMounted, inject, ref, computed } from "vue";
import { useRoute } from "vue-router";

export default {
  setup() {
    const api = inject("api");
    const i18n = inject("i18n");
    const amplitude = inject("amplitude");
    const route = useRoute();

    const { t, setLocale, locale } = i18n;

    const generation = ref(null);
    const generationId = ref(null);
    const loading = ref(false);
    const emojis = ["🔥", "😂", "😍", "😱"];
    const reactionCounts = ref({});
    const hasVoted = ref(false);
    const selectedEmoji = ref("");

    const modelName = computed(() => {
      if (!generation.value?.model?.name) return "";
      const name = generation.value.model.name;
      return name[locale.value] || name.en || "";
    });

    const isImage = computed(() => {
      return generation.value?.type?.startsWith("image") ?? true;
    });

    const openApp = () => {
      amplitude.track("click-main-button", {
        generation_id: generationId.value,
        model_id: generation.value.promptId,
      });
      window.location.href = `/open?modelId=${generation.value.promptId}`;
    };

    const vote = async (emoji) => {
      if (!generationId.value || emoji === selectedEmoji.value) return;

      const previousEmoji = selectedEmoji.value;

      // Optimistic update
      selectedEmoji.value = emoji;
      hasVoted.value = true;
      reactionCounts.value[emoji] = (reactionCounts.value[emoji] || 0) + 1;
      if (previousEmoji) {
        reactionCounts.value[previousEmoji] = Math.max(
          0,
          (reactionCounts.value[previousEmoji] || 1) - 1
        );
      }

      try {
        await api.voteGeneration(generationId.value, emoji);
        amplitude.track("vote", {
          generation_id: generationId.value,
          emoji,
        });
      } catch (error) {
        console.error("Failed to vote:", error);
        // Revert on error
        reactionCounts.value[emoji] = Math.max(
          0,
          (reactionCounts.value[emoji] || 1) - 1
        );
        if (previousEmoji) {
          reactionCounts.value[previousEmoji] =
            (reactionCounts.value[previousEmoji] || 0) + 1;
          selectedEmoji.value = previousEmoji;
        } else {
          hasVoted.value = false;
          selectedEmoji.value = "";
        }
      }
    };

    const fetchGeneration = async (id) => {
      loading.value = true;
      try {
        const response = await api.getGenerationById(id);
        generation.value = response.data;
        reactionCounts.value = response.data.reactions || {};

        // Restore visitor's previous vote if any
        if (response.data.visitorVote) {
          hasVoted.value = true;
          selectedEmoji.value = response.data.visitorVote;
        }

        // Register view after 3 seconds (filters bots that don't wait)
        setTimeout(() => {
          api.registerView(id).catch(() => { });
          // Track view event
          amplitude.track("view-generation", {
            generation_id: id,
            model_id: response.data.promptId,
            type: response.data.type,
          });
        }, 3000);
      } catch (error) {
        console.error("Failed to fetch generation:", error);
        window.location.href = "/open";
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      // Get language from query param (default: en)
      const queryLang = route.query.lang;
      if (queryLang === "fr" || queryLang === "en") {
        setLocale(queryLang);
      }

      const queryGenerationId = route.query.generationId;
      if (queryGenerationId) {
        generationId.value = queryGenerationId;
        fetchGeneration(queryGenerationId);
      } else {
        window.location.href = "/open";
      }
    });

    return {
      emojis,
      generation,
      hasVoted,
      isImage,
      loading,
      modelName,
      openApp,
      reactionCounts,
      selectedEmoji,
      t,
      vote,
    };
  },
};
</script>
